import type { List as ImmutableList, RecordOf } from 'immutable';
import _orderBy from 'lodash/orderBy';
import { createSelector } from 'reselect';

import { categoriesSelectors } from '../categories';
import { type CategoryProps, mkCategory, UsageTypeEnum } from '../categories/categoriesTypes';
import { entitlementsSelectors } from '../entitlements';
import { transactionRulesSelectors } from './transactionRulesSlice';
import type { TransactionRuleType } from './transactionRulesTypes';
import { getDuplicateTransactionRuleId, getFilteredTransactionRules, getRuleHash } from './transactionRulesUtils';

const getSortedRules = createSelector(transactionRulesSelectors.selectAll, (rules: TransactionRuleType[]) => {
  return _orderBy(rules, ['modifiedAt'], ['desc']);
});

// return a sorted list of rules with a hashkey which we will use to identify duplicate rules
export const getRules = createSelector(getSortedRules, (rules: TransactionRuleType[]) => {
  return rules.map((rule) => {
    return { ...rule, hashKey: getRuleHash(rule) };
  });
});

export const getDuplicateRuleId = createSelector(
  transactionRulesSelectors.selectAll,
  (_state: any, rule: TransactionRuleType) => rule,
  (rules, rule) => getDuplicateTransactionRuleId(rules, rule),
);

export const getFilteredRules = createSelector(
  transactionRulesSelectors.selectAll,
  (_state: any, searchValue: string) => searchValue,
  categoriesSelectors.getCategoriesById,
  (rules, searchValue, categories) => getFilteredTransactionRules(rules, searchValue, categories),
);

// determine if this rule has the given cat id in it
// TODO: if we ever implement category as a source condition, we need to update this logic
export const ruleHasCategoryId = (rule: TransactionRuleType, categoryIds: ImmutableList<string>): boolean => {
  return (rule.coaTarget && Boolean(categoryIds.find((id: string) => rule.coaTarget?.id === id))) || false;
};

export const getRulesMatchingPredicate = createSelector(
  (_state: unknown, predicate: (rule: TransactionRuleType) => boolean) => predicate,
  getRules,
  (predicate, rules) => rules.filter((rule) => predicate(rule)),
);

export const findEquivalentCategory = createSelector(
  categoriesSelectors.getCategoriesById,
  (_state: any, targetCategoryId: string) => targetCategoryId,
  (categories, targetCategoryId: string): Immutable.RecordOf<CategoryProps> | undefined => {
    const businessMap = new Map();
    const personalMap = new Map();
    const fullMap = new Map();
    const uncategorizedCoa = mkCategory({ id: '0', name: 'Uncategorized' });

    // Separate business and personal categories
    categories.forEach((cat) => {
      fullMap.set(cat.id, cat);
      if (cat.usageType === UsageTypeEnum.BUSINESS) {
        businessMap.set(cat.knownCategoryId, cat);
      } else if (cat.usageType === UsageTypeEnum.PERSONAL) {
        personalMap.set(cat.knownCategoryId, cat);
      }
    });

    const visited = new Set();

    function helper(currentId: string): RecordOf<CategoryProps> {
      if (!currentId || visited.has(currentId)) return uncategorizedCoa;
      visited.add(currentId);

      const current = fullMap.get(currentId);
      if (!current) return uncategorizedCoa;

      const isBusiness = current.usageType === UsageTypeEnum.BUSINESS;
      const oppositeMap = isBusiness ? personalMap : businessMap;

      const knownId = current.knownCategoryId;
      if (knownId) {
        for (const [_key, other] of oppositeMap.entries()) {
          if (other.knownCategoryId === knownId) {
            return other;
          }
        }
      }

      return current.parentId ? helper(current.parentId) : uncategorizedCoa;
    }

    return helper(targetCategoryId);
  },
);

export const getUseDefaultAccountInitialValue = createSelector(
  [
    (_state, input: Partial<TransactionRuleType>) => input,
    (state) => entitlementsSelectors.getBusinessEnabled(state),
    (state) => categoriesSelectors.getBusinessCoaIdsSet(state).size,
  ],
  (input, isBusinessEnabled, hasBusinessCategories) => {
    if (!hasBusinessCategories) {
      // Simplifi (Personal) user
      return undefined;
    }
    // edit case: Use the value from the rule
    if (input?.id) return !!input.useDefaultAccountUsage;
    /* create case:
     * QBP User : Allow to create rule with business category
     * Downgraded QBP User : Do not allow to create rule with business category
     */
    return isBusinessEnabled ? true : false;
  },
);
